// CreditsTable
import React from 'react';
import styles from './CreditsTable.module.sass';
import { StripePackage } from 'types/payment';
import { centsToDollarString } from 'utils/string';
import { postProductCheckout } from 'api/payment';
import { useAuth } from 'hooks/useAuth';
import cn from 'classnames';

export interface CreditsTableProps {
  creditPackages: StripePackage[];
}

const CreditsTable: React.FC<CreditsTableProps> = ({ creditPackages }) => {
  const { authState } = useAuth();
  const brand = authState.brand!;

  const handlePackagePurchase = (packageId: string) => {
    postProductCheckout(packageId, brand).then((url) =>
      window.location.assign(url)
    );
  };
  return (
    <div className={styles.creditsTable}>
      <div className={styles.tableHeader}>
        <div className={styles.creditsHeader}>Get up to</div>
        <div className={styles.priceHeader}>Price</div>
        <div className={styles.savingsHeader}>Savings</div>
        {/* empty cell for spacing with purchase button */}
        <div className={styles.purchaseHeader} />
      </div>
      {creditPackages.map((creditPackage, index) => {
        const { id } = creditPackage;
        const onClick = () => handlePackagePurchase(id);
        const isFirst = index === 0;

        return (
          <CreditPackageRow
            key={id}
            {...creditPackage}
            onClick={onClick}
            isFirst={isFirst}
          />
        );
      })}
      {/* Enterprise CTA */}
      <div className={styles.tableRow}>
        <div className={styles.contactCell}>
          <h2>looking for enterprise pricing?</h2>
          <p>
            Trend has custom packages for larger companies to get the best
            possible price on credits.
          </p>
        </div>
        <div className={cn(styles.purchaseCell, styles.purchaseCol)}>
          <a
            className={cn(styles.contactButton, 'button-3d')}
            target="_blank"
            rel="noreferrer"
            href="https://soona.co/contact-sales"
          >
            Chat with us
          </a>
        </div>
      </div>
    </div>
  );
};

const CreditPackageRow: React.FC<
  StripePackage & {
    onClick: () => void;
    isFirst: boolean;
  }
> = ({ id, cost, savings, credits, mostPopular, onClick, isFirst }) => {
  const numLevel1Partnerships = credits / 20;
  const numVideos = numLevel1Partnerships * 2;
  const numPhotos = numLevel1Partnerships * 5;
  const dollarCost = centsToDollarString(cost);
  const dollarSavings = centsToDollarString(savings);
  const showTag = mostPopular || isFirst;

  return (
    <div
      key={id}
      className={cn(styles.tableRow, {
        [styles.tagRow]: showTag,
      })}
    >
      <div className={styles.creditsCell}>
        {showTag && (
          <p className={styles.tag}>
            {mostPopular ? '🔥 most popular' : '🤑 biggest savings'}
          </p>
        )}
        <h2>
          {numVideos} videos / {numPhotos} photos
        </h2>
        <p>{credits} Credits</p>
      </div>
      <p className={styles.costCell}>{dollarCost}</p>
      <p className={styles.savingsCell}>{`${savings ? '~' : ''} ${dollarSavings}`}</p>
      <div className={styles.purchaseCell}>
        <button
          className={cn(styles.purchaseButton, 'button-3d')}
          onClick={onClick}
        >
          Buy
        </button>
      </div>
    </div>
  );
};

export default CreditsTable;
